import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Badge,
  IconButton,
  Input,
  Spinner,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ArrowUpIcon,
  CheckIcon,
  CloseIcon,
  DeleteIcon,
} from '@chakra-ui/icons';
import type { ValidationInfo, DeferredDocument } from 'types/documents';

export interface DocumentListRowProps {
  doc: DeferredDocument;
  mode: 'broker' | 'public';
  isUploading?: boolean;
  validationInfo?: ValidationInfo;
  fileInputRef?: (el: HTMLInputElement | null) => void;
  onUploadClick?: () => void;
  onFileChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onResolveClick?: () => void;
  onIgnoreClick?: () => void;
  onDeleteFile?: (fileId: number) => void;
}

const DocumentListRow = ({
  doc,
  mode,
  isUploading = false,
  validationInfo,
  fileInputRef,
  onUploadClick,
  onFileChange,
  onResolveClick,
  onIgnoreClick,
  onDeleteFile,
}: DocumentListRowProps) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const bgColor = useColorModeValue('white', 'navy.700');

  const isPending = doc.status === 'pending';
  const isReceived = doc.status === 'received';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'received':
        return 'blue';
      case 'resolved':
        return 'green';
      case 'auto_archived':
        return 'gray';
      case 'cannot_obtain':
        return 'red';
      case 'stale_ignored':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending Upload';
      case 'received':
        return 'Received';
      case 'resolved':
        return 'Resolved';
      case 'auto_archived':
        return 'Archived';
      case 'cannot_obtain':
        return 'Cannot Obtain';
      case 'stale_ignored':
        return 'Stale (Auto-Ignored)';
      default:
        return status;
    }
  };

  return (
    <Flex px="12px" py="10px" bg={bgColor} borderRadius="8px" align="center">
      {/* Document Name with Validation Badges */}
      <HStack flex="3" spacing="8px" align="center">
        {doc.validation_message ? (
          <Tooltip
            label={doc.validation_message}
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
            p="8px 12px"
            borderRadius="md"
          >
            <Text
              fontSize="sm"
              color={textColor}
              fontWeight="500"
              cursor="help"
              borderBottom="1px dotted"
              borderColor="gray.400"
            >
              {doc.text}
            </Text>
          </Tooltip>
        ) : (
          <Text fontSize="sm" color={textColor} fontWeight="500">
            {doc.text}
          </Text>
        )}
        {/* Validation Status Badge */}
        {validationInfo && (
          <>
            {(validationInfo.status === 'created' ||
              validationInfo.status === 'validating') && (
              <HStack spacing="4px">
                <Spinner size="xs" />
                <Text fontSize="xs" color="blue.500" whiteSpace="nowrap">
                  Validating...
                </Text>
              </HStack>
            )}
            {validationInfo.status === 'approved' && (
              <Badge colorScheme="green" fontSize="xs">
                ✓ Validated
              </Badge>
            )}
            {validationInfo.status === 'failed' && (
              <Tooltip
                label={validationInfo.result || 'Validation failed'}
                placement="top"
                hasArrow
                bg="orange.500"
                color="white"
                fontSize="sm"
                p="8px 12px"
                borderRadius="md"
              >
                <Badge colorScheme="orange" fontSize="xs" cursor="help">
                  ⚠ Warning
                </Badge>
              </Tooltip>
            )}
          </>
        )}
      </HStack>

      {/* Status */}
      <Box flex="1">
        <Badge colorScheme={getStatusColor(doc.status)} fontSize="xs">
          {getStatusLabel(doc.status)}
        </Badge>
      </Box>

      {/* Files List */}
      <Box flex="1">
        {doc.files.length > 0 ? (
          <VStack align="start" spacing="4px">
            {doc.files.map((file: any) => (
              <HStack key={file.id} spacing="4px">
                <Text fontSize="xs" color={textColor} isTruncated maxW="120px">
                  {file.name}
                </Text>
                {/* Only show delete button for broker mode and active statuses */}
                {mode === 'broker' &&
                  (doc.status === 'pending' || doc.status === 'received') &&
                  onDeleteFile && (
                    <IconButton
                      aria-label="Delete file"
                      icon={<DeleteIcon />}
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => onDeleteFile(file.id)}
                    />
                  )}
              </HStack>
            ))}
          </VStack>
        ) : (
          <Text fontSize="sm" color="gray.400">
            No files
          </Text>
        )}
      </Box>

      {/* Actions */}
      <HStack flex="1" spacing="8px">
        {mode === 'broker' ? (
          // Broker mode actions
          <>
            {isPending && (
              <>
                <IconButton
                  aria-label="Upload document"
                  icon={<ArrowUpIcon />}
                  size="sm"
                  colorScheme="blue"
                  variant="ghost"
                  isLoading={isUploading}
                  onClick={onUploadClick}
                />
                <IconButton
                  aria-label="Ignore document"
                  icon={<CloseIcon />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={onIgnoreClick}
                />
                <Input
                  type="file"
                  display="none"
                  ref={fileInputRef}
                  onChange={onFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
              </>
            )}
            {isReceived && (
              <IconButton
                aria-label="Mark as resolved"
                icon={<CheckIcon />}
                size="sm"
                colorScheme="green"
                variant="ghost"
                onClick={onResolveClick}
              />
            )}
          </>
        ) : (
          // Public mode actions - simplified upload only
          isPending && (
            <>
              <IconButton
                aria-label="Upload document"
                icon={<ArrowUpIcon />}
                size="sm"
                colorScheme="blue"
                variant="ghost"
                isLoading={isUploading}
                onClick={onUploadClick}
              />
              <Input
                type="file"
                display="none"
                ref={fileInputRef}
                onChange={onFileChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
            </>
          )
        )}
      </HStack>
    </Flex>
  );
};

export default DocumentListRow;
